import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { usePostsStore } from '../store/posts';
import { useProjectsStore } from '../store/projects';
import { api } from '../services/api';

interface DeletedFlight {
  id: string;
  display_id: string;
  name: string;
  project_id: string;
  project_name: string;
  start_date: string;
  end_date: string;
  status: string;
  deleted_at: string;
}

interface DeletedProject {
  id: string;
  display_id: string;
  name: string;
  slug: string;
  owner_type: string;
  deleted_at: string;
}

type TabType = 'posts' | 'flights' | 'projects';

export function Trash() {
  const { t } = useTranslation(['trash', 'projects', 'common']);
  const [activeTab, setActiveTab] = useState<TabType>('posts');
  const [deletedFlights, setDeletedFlights] = useState<DeletedFlight[]>([]);
  const [deletedProjects, setDeletedProjects] = useState<DeletedProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { deletedPosts, fetchDeletedPosts, restorePost, isLoading: postsLoading } = usePostsStore();
  const { projects, fetchProjects } = useProjectsStore();

  // Load data on mount and tab change
  useEffect(() => {
    fetchProjects();
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (activeTab === 'posts') {
        await fetchDeletedPosts();
      } else if (activeTab === 'flights') {
        const response = await api.get<{ data: DeletedFlight[] }>('/flights/trash');
        setDeletedFlights(response.data);
      } else if (activeTab === 'projects') {
        const response = await api.get<{ data: DeletedProject[] }>('/projects/trash');
        setDeletedProjects(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trash:loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestorePost = async (postId: string) => {
    try {
      await restorePost(postId);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trash:restoreError'));
    }
  };

  const handleRestoreFlight = async (flightId: string) => {
    try {
      await api.post(`/flights/${flightId}/restore`);
      setDeletedFlights(prev => prev.filter(f => f.id !== flightId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trash:restoreError'));
    }
  };

  const handleRestoreProject = async (projectId: string) => {
    try {
      await api.post(`/projects/${projectId}/restore`);
      setDeletedProjects(prev => prev.filter(p => p.id !== projectId));
      fetchProjects(); // Refresh projects list
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trash:restoreError'));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || projectId;
  };

  const tabs = [
    { id: 'posts' as TabType, label: t('trash:tabs.posts'), count: deletedPosts.length },
    { id: 'flights' as TabType, label: t('trash:tabs.flights'), count: deletedFlights.length },
    { id: 'projects' as TabType, label: t('trash:tabs.projects'), count: deletedProjects.length },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        }
        title={t('trash:title')}
        subtitle={t('trash:subtitleAutoDelete')}
        breadcrumbs={[{ label: 'Dashboard', href: '/' }]}
      />

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-4 text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {(isLoading || postsLoading) ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <>
          {/* Posts Tab */}
          {activeTab === 'posts' && (
            deletedPosts.length === 0 ? (
              <EmptyState type="posts" t={t} />
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:table.post')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:table.project')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:deletedAt')}</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('trash:table.action')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deletedPosts.map(post => (
                      <tr key={post.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            {post.media_urls?.[0] && (
                              <img
                                src={post.media_urls[0]}
                                alt=""
                                className="w-10 h-10 rounded object-cover mr-3"
                              />
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900 line-clamp-1">
                                {post.text || t('trash:table.noText')}
                              </p>
                              <p className="text-xs text-gray-500">{post.display_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {getProjectName(post.project_id)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate((post as any).deleted_at || post.updated_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleRestorePost(post.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {t('trash:actions.restore')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Flights Tab */}
          {activeTab === 'flights' && (
            deletedFlights.length === 0 ? (
              <EmptyState type="flights" t={t} />
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:table.flight')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:table.project')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:table.period')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:deletedAt')}</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('trash:table.action')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deletedFlights.map(flight => (
                      <tr key={flight.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{flight.name}</p>
                          <p className="text-xs text-gray-500">{flight.display_id}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {flight.project_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(flight.start_date).toLocaleDateString('de-DE')} - {new Date(flight.end_date).toLocaleDateString('de-DE')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(flight.deleted_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleRestoreFlight(flight.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {t('trash:actions.restore')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            deletedProjects.length === 0 ? (
              <EmptyState type="projects" t={t} />
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:table.project')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:table.type')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('trash:deletedAt')}</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('trash:table.action')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deletedProjects.map(project => (
                      <tr key={project.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{project.name}</p>
                          <p className="text-xs text-gray-500">{project.display_id}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {project.owner_type === 'own' ? t('trash:projectType.own') : t('trash:projectType.client')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(project.deleted_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleRestoreProject(project.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {t('trash:actions.restore')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}

function EmptyState({ type, t }: { type: 'posts' | 'flights' | 'projects'; t: (key: string, options?: Record<string, unknown>) => string }) {
  const labels = {
    posts: t('trash:tabs.posts'),
    flights: t('trash:tabs.flights'),
    projects: t('trash:tabs.projects'),
  };

  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
        <TrashIcon className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{t('trash:noDeletedItems', { type: labels[type] })}</h3>
      <p className="text-gray-500">
        {t('trash:noDeletedItemsDescription', { type: labels[type] })}
      </p>
    </div>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}
